/**
 * FC Custom Variant Dropdown
 * Progressively enhances the "Opties" variant option (regardless of picker type)
 * into a card-style dropdown with variant image, name, and price badge.
 * Does NOT modify product-option.liquid — layers on top of the existing markup.
 *
 * Developer: Alfie
 * Updated: 2026-03-17
 */
(function () {
  'use strict';

  var CONTAINER_SEL = '.main-product__block-variant_picker';
  var TARGET_OPTION = 'opties';

  function init() {
    var container = document.querySelector(CONTAINER_SEL);
    if (!container) return;

    var variantPicker = container.querySelector('[data-variant-picker]');
    if (!variantPicker) return;

    var variantsScript = variantPicker.querySelector('#productVariants');
    if (!variantsScript) return;

    var variants = JSON.parse(variantsScript.textContent);
    var minPrice = Math.min.apply(null, variants.map(function (v) { return v.price; }));
    var moneyFormat = getMoneyFormat();

    /* Find the "Opties" option — could be variant-button, variant-select, or variant-color */
    var optionWrappers = variantPicker.querySelectorAll('.m-product-option');
    optionWrappers.forEach(function (wrapper) {
      var pickerEl = wrapper.querySelector('[data-option-name]');
      if (!pickerEl) return;
      var optionName = (pickerEl.dataset.optionName || '').toLowerCase();
      if (optionName !== TARGET_OPTION) return;
      enhance(wrapper, pickerEl, variants, minPrice, moneyFormat);
    });
  }

  function enhance(wrapper, pickerEl, variants, minPrice, moneyFormat) {
    /* Determine option position from the first node */
    var firstNode = wrapper.querySelector('[data-option-position]');
    var posNum = firstNode ? firstNode.dataset.optionPosition : '1';
    var optionKey = 'option' + posNum;

    /* Gather option data from radio inputs or select options */
    var optionsData = buildOptionsData(wrapper, variants, optionKey, minPrice);
    if (optionsData.length === 0) return;

    /* Build and insert custom dropdown */
    var fcDropdown = createDropdownUI(wrapper, pickerEl, optionsData, moneyFormat);
    wrapper.appendChild(fcDropdown);

    /* Hide original option content — use class with !important to beat theme utilities */
    var contentDiv = wrapper.querySelector('.m-product-option--content');
    if (contentDiv) contentDiv.classList.add('fc-dropdown-hidden');

    /* Listen for external changes (variant-picker.js may update radios/select) */
    observeChanges(wrapper, fcDropdown, optionsData, moneyFormat);
  }

  /* ------------------------------------------------------------------ */
  /*  DATA                                                               */
  /* ------------------------------------------------------------------ */

  function buildOptionsData(wrapper, variants, optionKey, minPrice) {
    var data = [];

    /* Try radio inputs first (button, color, image picker types) */
    var radioNodes = wrapper.querySelectorAll('.m-product-option--node');
    if (radioNodes.length > 0) {
      radioNodes.forEach(function (node) {
        var input = node.querySelector('input[type="radio"]');
        if (!input) return;
        var value = input.value;
        var matchingVariant = variants.find(function (v) { return v[optionKey] === value; });
        data.push(buildOptionItem(value, value, matchingVariant, minPrice));
      });
      return data;
    }

    /* Fallback: select element (dropdown picker type) */
    var selectEl = wrapper.querySelector('select');
    if (selectEl) {
      Array.from(selectEl.options).forEach(function (opt) {
        var value = opt.value;
        var matchingVariant = variants.find(function (v) { return v[optionKey] === value; });
        data.push(buildOptionItem(value, opt.textContent.trim(), matchingVariant, minPrice));
      });
    }

    return data;
  }

  function buildOptionItem(value, text, matchingVariant, minPrice) {
    var imageSrc = null;
    if (matchingVariant && matchingVariant.featured_image && matchingVariant.featured_image.src) {
      imageSrc = matchingVariant.featured_image.src;
    }
    return {
      value: value,
      text: text,
      image: imageSrc,
      price: matchingVariant ? matchingVariant.price : 0,
      priceDiff: matchingVariant ? matchingVariant.price - minPrice : 0
    };
  }

  function getSelectedValue(wrapper) {
    /* Check radios */
    var checkedRadio = wrapper.querySelector('.m-product-option--node input[type="radio"]:checked');
    if (checkedRadio) return checkedRadio.value;

    /* Check select */
    var selectEl = wrapper.querySelector('select');
    if (selectEl) return selectEl.value;

    return null;
  }

  /* ------------------------------------------------------------------ */
  /*  UI                                                                 */
  /* ------------------------------------------------------------------ */

  function createDropdownUI(wrapper, pickerEl, optionsData, moneyFormat) {
    var selectedValue = getSelectedValue(wrapper);
    var selectedOpt = optionsData.find(function (o) { return o.value === selectedValue; }) || optionsData[0];

    var el = document.createElement('div');
    el.className = 'fc-dropdown';

    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'fc-dropdown__trigger';
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.innerHTML = renderTrigger(selectedOpt, moneyFormat);

    var list = document.createElement('div');
    list.className = 'fc-dropdown__list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    list.innerHTML = optionsData.map(function (opt) {
      var isSelected = opt.value === selectedValue;
      return '<div class="fc-dropdown__option' + (isSelected ? ' fc-dropdown__option--active' : '') +
        '" role="option" aria-selected="' + isSelected + '" data-value="' + escapeAttr(opt.value) + '">' +
        renderOptionInner(opt, moneyFormat) + '</div>';
    }).join('');

    el.appendChild(trigger);
    el.appendChild(list);

    /* --- Events --- */
    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      var isOpen = trigger.getAttribute('aria-expanded') === 'true';
      toggle(trigger, list, !isOpen);
    });

    list.addEventListener('click', function (e) {
      var optEl = e.target.closest('.fc-dropdown__option');
      if (!optEl) return;

      var value = optEl.dataset.value;

      /* Update the original hidden inputs */
      setOriginalValue(wrapper, value);

      /* Update active state in dropdown */
      list.querySelectorAll('.fc-dropdown__option').forEach(function (o) {
        o.classList.remove('fc-dropdown__option--active');
        o.setAttribute('aria-selected', 'false');
      });
      optEl.classList.add('fc-dropdown__option--active');
      optEl.setAttribute('aria-selected', 'true');

      var opt = optionsData.find(function (o) { return o.value === value; });
      if (opt) trigger.innerHTML = renderTrigger(opt, moneyFormat);

      toggle(trigger, list, false);
    });

    /* Close on outside click */
    document.addEventListener('click', function (e) {
      if (!el.contains(e.target)) {
        toggle(trigger, list, false);
      }
    });

    /* Keyboard: Escape to close */
    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        toggle(trigger, list, false);
        trigger.focus();
      }
    });

    return el;
  }

  function setOriginalValue(wrapper, value) {
    /* Try radio inputs */
    var radios = wrapper.querySelectorAll('.m-product-option--node input[type="radio"]');
    if (radios.length > 0) {
      radios.forEach(function (radio) {
        if (radio.value === value) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          /* Also click the label to trigger variant-picker.js listeners */
          var label = radio.nextElementSibling;
          if (label) label.click();
        }
      });
      return;
    }

    /* Fallback: select element */
    var selectEl = wrapper.querySelector('select');
    if (selectEl) {
      selectEl.value = value;
      selectEl.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function toggle(trigger, list, open) {
    trigger.setAttribute('aria-expanded', String(open));
    list.hidden = !open;
    if (open) {
      trigger.classList.add('fc-dropdown__trigger--open');
    } else {
      trigger.classList.remove('fc-dropdown__trigger--open');
    }
  }

  /* ------------------------------------------------------------------ */
  /*  OBSERVE EXTERNAL CHANGES                                           */
  /* ------------------------------------------------------------------ */

  function observeChanges(wrapper, fcDropdown, optionsData, moneyFormat) {
    var contentDiv = wrapper.querySelector('.m-product-option--content');
    if (!contentDiv) return;

    /* Watch for attribute changes on radio inputs (checked state) */
    var observer = new MutationObserver(function () {
      syncUI(wrapper, fcDropdown, optionsData, moneyFormat);
    });
    observer.observe(contentDiv, { attributes: true, subtree: true, attributeFilter: ['checked'] });

    /* Also listen to change events bubbling up */
    contentDiv.addEventListener('change', function () {
      syncUI(wrapper, fcDropdown, optionsData, moneyFormat);
    });
  }

  function syncUI(wrapper, fcDropdown, optionsData, moneyFormat) {
    var value = getSelectedValue(wrapper);
    if (!value) return;

    var trigger = fcDropdown.querySelector('.fc-dropdown__trigger');
    var list = fcDropdown.querySelector('.fc-dropdown__list');

    list.querySelectorAll('.fc-dropdown__option').forEach(function (o) {
      var isActive = o.dataset.value === value;
      o.classList.toggle('fc-dropdown__option--active', isActive);
      o.setAttribute('aria-selected', String(isActive));
    });

    var opt = optionsData.find(function (o) { return o.value === value; });
    if (opt && trigger) {
      /* Preserve open/close state */
      var isOpen = trigger.getAttribute('aria-expanded') === 'true';
      trigger.innerHTML = renderTrigger(opt, moneyFormat);
      if (isOpen) trigger.classList.add('fc-dropdown__trigger--open');
    }
  }

  /* ------------------------------------------------------------------ */
  /*  RENDER HELPERS                                                     */
  /* ------------------------------------------------------------------ */

  function renderTrigger(opt, moneyFormat) {
    return renderOptionInner(opt, moneyFormat) +
      `<svg class="fc-dropdown__chevron" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M13.5306 6.53073L8.5306 11.5307C8.46092 11.6007 8.37813 11.6561 8.28696 11.694C8.1958 11.7318 8.09806 11.7513 7.99935 11.7513C7.90064 11.7513 7.8029 11.7318 7.71173 11.694C7.62057 11.6561 7.53778 11.6007 7.4681 11.5307L2.4681 6.53073C2.3272 6.38984 2.24805 6.19874 2.24805 5.99948C2.24805 5.80023 2.3272 5.60913 2.4681 5.46823C2.60899 5.32734 2.80009 5.24818 2.99935 5.24818C3.19861 5.24818 3.3897 5.32734 3.5306 5.46823L7.99997 9.93761L12.4693 5.46761C12.6102 5.32671 12.8013 5.24756 13.0006 5.24756C13.1999 5.24756 13.391 5.32671 13.5318 5.46761C13.6727 5.60851 13.7519 5.7996 13.7519 5.99886C13.7519 6.19812 13.6727 6.38921 13.5318 6.53011L13.5306 6.53073Z" fill="#01287E"/>
      </svg>`;
  }

  function renderOptionInner(opt, moneyFormat) {
    var imgHtml = '';
    if (opt.image) {
      var src = getSizedImageUrl(opt.image, 130);
      imgHtml = '<img class="fc-dropdown__image" src="' + src +
        '" alt="' + escapeAttr(opt.text) + '" width="61" height="61" loading="lazy">';
    }

    var badgeHtml = '';
    if (opt.price > 0) {
      var prefix = opt.priceDiff > 0 ? '+' : '';
      badgeHtml = '<span class="fc-dropdown__badge">' + prefix + formatMoney(opt.priceDiff > 0 ? opt.priceDiff : opt.price, moneyFormat) + '</span>';
    }

    return '<div class="fc-dropdown__inner">' +
      imgHtml +
      '<div class="fc-dropdown__info">' +
      '<span class="fc-dropdown__name">' + escapeHtml(opt.text) + '</span>' +
      badgeHtml +
      '</div></div>';
  }

  /* ------------------------------------------------------------------ */
  /*  UTILITIES                                                          */
  /* ------------------------------------------------------------------ */

  function getMoneyFormat() {
    if (window.MinimogSettings && window.MinimogSettings.money_format) {
      return window.MinimogSettings.money_format;
    }
    if (window.Shopify && window.Shopify.money_format) {
      return window.Shopify.money_format;
    }
    if (window.theme && window.theme.moneyFormat) {
      return window.theme.moneyFormat;
    }
    return '\u20AC{{amount_with_comma_separator}}';
  }

  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2);
    var amountComma = amount.replace('.', ',');
    var amountNoDecimals = Math.floor(cents / 100).toString();

    return format
      .replace('{{amount_with_comma_separator}}', amountComma.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace('{{amount}}', amount.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_with_apostrophe_separator}}', amount.replace(/\B(?=(\d{3})+(?!\d))/g, "'"))
      .replace('{{amount_no_decimals_with_space_separator}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ' '))
      .replace('{{amount_with_space_separator}}', amount.replace(/\B(?=(\d{3})+(?!\d))/g, ' '));
  }

  function getSizedImageUrl(src, width) {
    if (!src) return '';
    if (src.indexOf('cdn.shopify.com') !== -1) {
      var url = new URL(src.indexOf('//') === 0 ? 'https:' + src : src);
      url.searchParams.set('width', width);
      return url.toString();
    }
    return src;
  }

  function escapeHtml(str) {
    var el = document.createElement('span');
    el.textContent = str || '';
    return el.innerHTML;
  }

  function escapeAttr(str) {
    return (str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ------------------------------------------------------------------ */
  /*  ATC BUTTON PRICE — update [data-fc-atc-price] on variant change    */
  /* ------------------------------------------------------------------ */

  function formatMoneyNoTrailingZeros(cents, format) {
    var formatted = formatMoney(cents, format);
    /* Strip ,00 or .00 trailing zeros (e.g. €549,00 → €549) */
    return formatted.replace(/[,.]00$/, '');
  }

  function initAtcPrice() {
    var priceEl = document.querySelector('[data-fc-atc-price]');
    if (!priceEl) return;

    var form = priceEl.closest('form') || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    var hiddenInput = form.querySelector('input[name="id"]');
    if (!hiddenInput) return;

    var variantPicker = document.querySelector('[data-variant-picker]');
    if (!variantPicker) return;

    var variantsScript = variantPicker.querySelector('#productVariants');
    if (!variantsScript) return;

    var variants = JSON.parse(variantsScript.textContent);
    var moneyFormat = getMoneyFormat();

    function updatePrice() {
      var vid = parseInt(hiddenInput.value, 10);
      var variant = variants.find(function (v) { return v.id === vid; });
      if (variant) {
        priceEl.textContent = formatMoneyNoTrailingZeros(variant.price, moneyFormat);
      }
    }

    /* Watch the hidden variant ID input for changes */
    var observer = new MutationObserver(updatePrice);
    observer.observe(hiddenInput, { attributes: true, attributeFilter: ['value'] });

    /* Also listen for change events on the input */
    hiddenInput.addEventListener('change', updatePrice);
  }

  /* ------------------------------------------------------------------ */
  /*  INIT                                                               */
  /* ------------------------------------------------------------------ */

  function initAll() {
    init();
    initAtcPrice();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
